import { html, render } from 'lit'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { getSettings } from '@app/shared/settings'

/**
 * Show generic dialog
 * @param title
 * @param message
 * @returns Promise
 */
export const dialog = async (options: { id?: string; header: string; message: string; modal?: boolean; closeText?: string }) => {
  await Promise.all([import('@app/components/dialog/cc-dialog.component'), import('@app/components/button/cc-button.component')])

  const { promise, resolve, reject } = Promise.withResolvers<boolean>()

  const { header, message, modal = false, closeText = 'Close', id = 'dialog' } = options

  // Don't open dialog if another is already opened
  if (document.body.querySelector(`cc-dialog#${id}`)) {
    reject('Dialog already opened')
    return
  }

  const template = html`
    ${unsafeHTML(message)}
    <cc-button slot="footer" variant="primary" autofocus cc-dialog-close="true">${closeText}</cc-button>
  `

  const dialog = Object.assign(document.createElement('cc-dialog'), {
    id,
    header,
    modal,
  })

  // Render template in dialog
  render(template, dialog)

  // On hide complete remove dialog from DOM
  dialog.addEventListener('cc-after-hide', () => dialog.remove())

  // Resolve when dialog hide
  dialog.addEventListener('cc-hide', async () => resolve(dialog.returnValue === 'true'))

  // Render dialog in body
  document.body.appendChild(dialog)

  // Show dialog
  dialog.show()

  return promise
}

/**
 * Show confirmation dialog
 * @param options
 * @returns Promise
 */
export const confirmDialog = async (options: { header: string; message: string; cancelText?: string; confirmText?: string }) => {
  await Promise.all([import('@app/components/dialog/cc-dialog.component'), import('@app/components/button/cc-button.component')])

  const { promise, resolve, reject } = Promise.withResolvers<boolean>()

  // Don't open dialog if another is already opened
  if (document.body.querySelector('cc-dialog#confirm-dialog')) {
    reject('Dialog already opened')
    return
  }

  const { header, message, cancelText = 'Cancel', confirmText = 'Confirm' } = options

  const template = html`
    ${unsafeHTML(message)}
    <cc-button slot="footer" variant="secondary" cc-dialog-close="false">${cancelText}</cc-button>
    <cc-button slot="footer" variant="primary" autofocus cc-dialog-close="true">${confirmText}</cc-button>
  `

  const dialog = Object.assign(document.createElement('cc-dialog'), {
    id: 'confirm-dialog',
    header,
    modal: true,
  })

  // Render template in dialog
  render(template, dialog)

  // On hide complete remove dialog from DOM
  dialog.addEventListener('cc-after-hide', () => dialog.remove())

  // Resolve when dialog hide
  dialog.addEventListener('cc-hide', async () => resolve(dialog.returnValue === 'true'))

  // Append dialog to body
  document.body.appendChild(dialog)

  // Show dialog
  dialog.show()

  return promise
}

/**
 * Show confirmation dialog with input
 * @param options
 * @returns Promise
 */
export const promptDialog = async (options: { header: string; message: string; promt?: string; cancelText?: string; confirmText?: string }) => {
  await Promise.all([
    import('@app/components/dialog/cc-dialog.component'),
    import('@app/components/button/cc-button.component'),
    import('@app/components/input/cc-input.component'),
  ])

  const { promise, resolve, reject } = Promise.withResolvers<boolean>()

  // Don't open dialog if another is already opened
  if (document.body.querySelector('cc-dialog#confirm-input-dialog')) {
    reject('Dialog already opened')
    return
  }

  const { header, message, promt = 'confirm', cancelText = 'Cancel', confirmText = 'Confirm' } = options

  const template = html`
    ${unsafeHTML(message)}
    <br /><br />
    <cc-input placeholder="Type '${promt}'" pattern="${promt}" required autofocus></cc-input>
    <cc-button slot="footer" variant="secondary" cc-dialog-close="false">${cancelText}</cc-button>
    <cc-button slot="footer" variant="primary" id="confirm">${confirmText}</cc-button>
  `

  const dialog = Object.assign(document.createElement('cc-dialog'), {
    id: 'confirm-input-dialog',
    header,
    modal: true,
  })

  // Render template in dialog
  render(template, dialog)

  // On hide complete remove dialog from DOM
  dialog.addEventListener('cc-after-hide', () => dialog.remove())

  // Input element
  const input = dialog.querySelector('cc-input')!

  // Validate input on confirm click
  dialog.querySelector('#confirm')?.addEventListener('click', () => {
    if (!input.checkValidity()) {
      input.focus()
    } else {
      dialog.hide('true')
    }
  })

  // Resolve when dialog hide
  dialog.addEventListener('cc-hide', async () => resolve(dialog.returnValue === 'true'))

  // Append dialog to body
  document.body.appendChild(dialog)

  // Show dialog
  dialog.show()

  return promise
}

/**
 * Show custom error dialog
 * @param options
 * @returns Promise
 */
export const customErrorDialog = async (options: { header: string; code: number; message?: string }) => {
  await Promise.all([import('@app/components/dialog/cc-dialog.component'), import('@app/components/button/cc-button.component')])

  const { promise, resolve, reject } = Promise.withResolvers<void>()

  // Don't open dialog if another is already opened
  if (document.body.querySelector('cc-dialog#custom-error-dialog')) {
    reject('Dialog already opened')
    return
  }

  const { header, message, code } = options

  // Setup default action button text
  let actionText = 'Close'
  let defaultMessage = message

  if ([500].includes(code)) {
    const settings = await getSettings()
    defaultMessage = `An unexpected error occurred. Please refresh the page and try again. If the error persists, contact the competition administrator at ${settings?.supportEmail}.`
  }

  if ([408, 500].includes(code)) {
    actionText = 'Refresh Page'
  }

  const template = html`
    ${defaultMessage}
    <cc-button slot="footer" variant="secondary" cc-dialog-close>Dismiss</cc-button>
    <cc-button slot="footer" variant="primary" autofocus id="action-button" cc-dialog-close>${actionText}</cc-button>
  `

  const dialog = Object.assign(document.createElement('cc-dialog'), {
    id: 'custom-error-dialog',
    header,
    modal: true,
  })

  // Render template in dialog
  render(template, dialog)

  // On hide complete remove dialog from DOM
  dialog.addEventListener('cc-after-hide', () => dialog.remove())

  // Resolve when dialog hide
  dialog.addEventListener('cc-hide', async () => resolve())

  // Action button
  dialog.querySelector('#action-button')?.addEventListener('click', () => {
    if ([408, 500].includes(code)) {
      location.reload()
    }
  })

  // Render dialog in body
  document.body.appendChild(dialog)

  // Show dialog
  dialog.show()

  return promise
}
